import {
  HttpStatus,
  ResponseFormatModel,
} from "@/app/lib/models/ResponseFormatModel";
import { UserDetailsModel } from "@/app/lib/models/UserDetailsModel";
import { AppConstants } from "@/app/lib/util/AppConstants";
import { MemberDetailsModel } from "@/app/lib/models/MemberDetailsModel";
import { getAuthHeaders, getBaseUrl } from "@/app/services/api.utils";
import CookieSessionManager from "@/app/services/session.client";
import {
  apiRequest,
  logoutOnUnauthorized,
} from "@/app/services/api_base.service";
import { UserSystemDetailsModel } from "@/app/lib/models/UserSystemDetailsModel";
import {
  ACCESS_SUSPENDED_CODE,
  ACCESS_SUSPENDED_MESSAGE,
  type AccessSuspensionSource,
} from "@/app/lib/util/access-suspension.util";
import logger from "@/lib/logger";

const BASE_PATH = getBaseUrl() + "/v1/users";
/**
 * Get one user's details.
 * @param email_id Target user email, or self when callerEmailID is omitted.
 * @param sessionId Optional session id.
 * @param callerEmailID When set (admin/center head), authenticates as caller and loads email_id via target_email_id.
 */
export const getUserDetailsApi = async (
  email_id: string,
  sessionId?: string,
  callerEmailID?: string,
  logoutOnAuthError: boolean = true,
): Promise<ResponseFormatModel> => {
  let response: ResponseFormatModel = {
    message: "",
    status: HttpStatus.BadRequest,
  };

  try {
    let headers: any = await getAuthHeaders(
      sessionId,
      callerEmailID || email_id,
    );
    let url = `${BASE_PATH}/user-details`;
    if (callerEmailID) {
      headers["email_id"] = callerEmailID;
      url = `${BASE_PATH}/user-details?target_email_id=${encodeURIComponent(email_id)}`;
    } else {
      headers["email_id"] = email_id;
    }

    const resp: ResponseFormatModel = await apiRequest(url, "GET", headers);

    // Backend sends this gate as HTTP 200 with the 403 only in the body.
    if (resp?.data?.code === ACCESS_SUSPENDED_CODE) {
      return new ResponseFormatModel(
        HttpStatus.Forbidden,
        ACCESS_SUSPENDED_MESSAGE,
        { code: ACCESS_SUSPENDED_CODE },
      );
    }

    if (resp?.status === HttpStatus.OK && resp.data) {
      return resp;
    }

    response = resp;
    throw new Error(resp.message || "Error fetching user details");
  } catch (error: any) {
    if (error?.response) {
      response = {
        message: error.response.data?.message || "Server error",
        status: error.response.status,
        data: null,
      };
    } else if (!response.status || response.status === HttpStatus.BadRequest) {
      response = {
        message: "Cannot connect to server. Please try again later.",
        status: HttpStatus.ServiceUnavailable,
        data: null,
      };
    }

    if (logoutOnAuthError) {
      await logoutOnUnauthorized(response);
    }
    logger.error("Error fetching user:", String(error));
  }

  return response;
};

export interface SetTraineeAccessSuspensionBody {
  trainee_email: string;
  mode: "suspend" | "restore";
  source?: AccessSuspensionSource;
  session_id?: string;
  member_id?: string;
}

export const setTraineeAccessSuspensionApi = async (
  callerEmailId: string,
  body: SetTraineeAccessSuspensionBody,
  sessionId?: string,
): Promise<ResponseFormatModel> => {
  try {
    const headers: any = await getAuthHeaders(sessionId, callerEmailId);
    headers["email_id"] = callerEmailId;
    const resp: ResponseFormatModel = await apiRequest(
      `${BASE_PATH}/trainee/access-suspension`,
      "POST",
      headers,
      body,
    );
    await logoutOnUnauthorized(resp);
    return resp;
  } catch (error: any) {
    logger.error("Error updating trainee access suspension:", String(error));
    return {
      status: HttpStatus.ServiceUnavailable,
      message: "Cannot connect to server. Please try again later.",
      data: null,
    };
  }
};

export const resolveAccessLinkApi = async (
  token: string,
): Promise<ResponseFormatModel> => {
  try {
    return await apiRequest(
      `${BASE_PATH}/access-link/resolve`,
      "POST",
      { "Content-Type": "application/json" },
      { token },
    );
  } catch (error: any) {
    logger.error("Error resolving access link:", String(error));
    return {
      status: HttpStatus.ServiceUnavailable,
      message: "Cannot connect to server. Please try again later.",
      data: null,
    };
  }
};

/**
 * Get all the Guardians or trainees
 * @param type = "Guardians" or "Trainees"
 * @param email_Id = email id of the user
 * @param sessionId = session id
 */
export const getAllUsersApi = async (
  type: string,
  email_Id: string,
  sessionId?: string,
): Promise<ResponseFormatModel> => {
  let response: ResponseFormatModel = {
    message: "",
    status: HttpStatus.BadRequest,
  };
  try {
    let headers: any = await getAuthHeaders(sessionId, email_Id);
    headers["email_id"] = email_Id;
    let resp: any;
    if (type === AppConstants.Roles.GUARDIAN) {
      const url = `${BASE_PATH}/guardians`;
      response = await apiRequest(url, "GET", headers);
    } else if (type === AppConstants.Roles.TRAINEE) {
      const url = `${BASE_PATH}/trainees`;
      response = await apiRequest(url, "GET", headers);
    } else if (type === AppConstants.Roles.CENTER_HEAD) {
      const url = `${BASE_PATH}/center-heads`;
      response = await apiRequest(url, "GET", headers);
    }
    if (response && response.status === HttpStatus.OK) {
      return {
        ...response,
        data: Array.isArray(response.data) ? response.data : [],
      };
    }
    logger.warn("Unexpected response fetching users list", {
      type,
      status: response?.status,
    });
    return {
      message: response?.message ?? "",
      status: HttpStatus.OK,
      data: [],
    };
  } catch (error) {
    logger.error("Error fetching all users of type", {
      type,
      error: String(error),
    });
    return { message: "", status: HttpStatus.OK, data: [] };
  }
};

/**
 * Get all the trainees for a guardian
 * @param email_Id : email id of the guardian
 * @param sessionId : session id
 * @param adminEmailID - Admin email id if the request is made by admin
 */
export const getTraineesForGuardianApi = async (
  email_Id: string,
  sessionId?: string,
  adminEmailID?: string,
): Promise<ResponseFormatModel> => {
  let response: ResponseFormatModel = {
    message: "",
    status: HttpStatus.BadRequest,
  };
  try {
    const authEmail = adminEmailID || email_Id;
    let headers: any = await getAuthHeaders(sessionId, authEmail);
    if (adminEmailID) {
      headers["email_id"] = adminEmailID;
      headers["user_id"] = email_Id;
    } else {
      headers["email_id"] = email_Id;
    }
    const url = `${BASE_PATH}/guardian/trainees`;
    response = await apiRequest(url, "GET", headers);
    if (response && response.status === HttpStatus.OK && response.data) {
      return response;
    } else if (response && response.status === HttpStatus.NotFound) {
      // Backward compatibility: older backend returned 404 for empty trainee list.
      return new ResponseFormatModel(HttpStatus.OK, "Success", []);
    } else {
      throw new Error("Error fetching all Trainees for Guardian -- ");
    }
  } catch (error) {
    // Don't force logout for this API; failure here is non-auth critical.
    logger.error(
      "Error fetching all Trainees for Guardian -- :",
      String(error),
    );
    // throw new Error(error);
  }
  return response;
};
/**
 * Get Count of  all the trainees for a guardian
 * @param email_Id : email id of the guardian
 * @param sessionId : session id
 * @param adminEmailID - Admin email id if the request is made by admin
 */
export const getCountOfTraineesForGuardianApi = async (
  email_Id: string,
  sessionId?: string,
  adminEmailID?: string,
): Promise<ResponseFormatModel> => {
  let response: ResponseFormatModel = {
    message: "",
    status: HttpStatus.BadRequest,
  };
  try {
    const authEmail = adminEmailID || email_Id;
    let headers: any = await getAuthHeaders(sessionId, authEmail);
    if (adminEmailID) {
      headers["email_id"] = adminEmailID;
      headers["user_id"] = email_Id;
    } else {
      headers["email_id"] = email_Id;
    }
    const url = `${BASE_PATH}/trainees/count`;
    response = await apiRequest(url, "GET", headers);
    if (response && response.status === HttpStatus.OK && response.data) {
      return response;
    } else {
      throw new Error("Error fetching getCountOfTraineesForGuardianApi -- ");
    }
  } catch (error) {
    await logoutOnUnauthorized(response);
    logger.error(
      "Error fetching getCountOfTraineesForGuardianApi -- :",
      String(error),
    );
  }
  return response;
};

/**
 * Get all the trainees for a guardian
 * @param email_Id : email id of the guardian
 * @param sessionId : session id
 * @param adminEmailID - Admin email id if the request is made by admin
 */
export const getGuardiansForTraineesApi = async (
  email_Id: string,
  sessionId?: string,
  adminEmailID?: string,
): Promise<ResponseFormatModel> => {
  let response: ResponseFormatModel = {
    message: "",
    status: HttpStatus.BadRequest,
  };
  try {
    const authEmail = adminEmailID || email_Id;
    let headers: any = await getAuthHeaders(sessionId, authEmail);
    if (adminEmailID) {
      headers["email_id"] = adminEmailID;
      headers["user_id"] = email_Id;
    } else {
      headers["email_id"] = email_Id;
    }
    const url = `${BASE_PATH}/trainee/guardians`;
    response = await apiRequest(url, "GET", headers);
    if (response && response.status === HttpStatus.OK && response.data) {
      return response;
    } else {
      throw new Error("Error fetching getGuardiansForTrainees -- ");
    }
  } catch (error) {
    await logoutOnUnauthorized(response);
    logger.error("Error fetching getGuardiansForTrainees - :", String(error));
  }
  return response;
};

/**
 * To register a new user.
 * @param traineeDetails : UserDetailsModel - New Registration form data.
 * @param guardianDetails
 */
export const registerAccountsApi = async (
  traineeDetails: UserDetailsModel,
  guardianDetails: UserDetailsModel,
): Promise<ResponseFormatModel> => {
  let response: ResponseFormatModel = {
    message: "",
    status: HttpStatus.BadRequest,
  };

  const data = {
    traineeDetails: traineeDetails,
    guardianDetails: guardianDetails,
  };
  const headersForReq: Record<string, string> = {
    "Content-Type": "application/json",
  };
  const url = `${BASE_PATH}/user/register`;

  try {
    response = await apiRequest(url, "POST", headersForReq, data);
    if (response.status === HttpStatus.OK && response.data) {
      return response;
    } else {
      logger.error(`Error Registering New Users -- : ${response}`);
      throw new Error("Error Registering New Users -- ");
    }
  } catch (error) {
    await logoutOnUnauthorized(response);
    logger.error("Error Registering New Users - :" + error);
    response = new ResponseFormatModel(500, "Server Error", null);
  }

  return response;
};

/**
 * To create a new user by admin with Firebase Auth and Database.
 * This API creates the user in Firebase Auth first, then registers in the database.
 * @param traineeDetails : UserDetailsModel - New user details with password
 * @param guardianDetails : UserDetailsModel - Guardian details
 * @param sessionId : Optional session ID
 */
export const createUserByAdminApi = async (
  traineeDetails: UserDetailsModel,
  guardianDetails: UserDetailsModel,
  sessionId?: string,
  callerEmailId?: string,
): Promise<ResponseFormatModel> => {
  let response: ResponseFormatModel = {
    message: "",
    status: HttpStatus.BadRequest,
  };

  const data = {
    traineeDetails: traineeDetails,
    guardianDetails: guardianDetails,
  };

  const headersForReq = await getAuthHeaders(sessionId, callerEmailId);
  headersForReq["Content-Type"] = "application/json";

  const url = `${BASE_PATH}/user/admin/create-user`;

  try {
    response = await apiRequest(url, "POST", headersForReq, data);
    if (response.status === HttpStatus.OK && response.data) {
      return response;
    } else {
      logger.error(`Error Creating User by Admin -- : ${response}`);
      throw new Error("Error Creating User by Admin -- ");
    }
  } catch (error) {
    logger.error("Error Creating User by Admin - :" + error);
    response = new ResponseFormatModel(500, "Server Error", null);
  }

  return response;
};

/**
 * To create a standalone guardian account by admin/center head.
 * @param guardianDetails : UserDetailsModel - Guardian details with email_id and password
 * @param sessionId : Optional session ID
 */
export const createGuardianByAdminApi = async (
  guardianDetails: UserDetailsModel,
  sessionId?: string,
  callerEmailId?: string,
): Promise<ResponseFormatModel> => {
  let response: ResponseFormatModel = {
    message: "",
    status: HttpStatus.BadRequest,
  };

  const headersForReq = await getAuthHeaders(sessionId, callerEmailId);
  headersForReq["Content-Type"] = "application/json";

  const url = `${BASE_PATH}/user/admin/create-guardian`;

  try {
    response = await apiRequest(url, "POST", headersForReq, guardianDetails);
    if (response.status === HttpStatus.OK && response.data) {
      return response;
    } else {
      logger.error(`Error Creating Guardian by Admin -- : ${response}`);
      throw new Error("Error Creating Guardian by Admin -- ");
    }
  } catch (error) {
    logger.error("Error Creating Guardian by Admin - :" + error);
    response = new ResponseFormatModel(500, "Server Error", null);
  }

  return response;
};

/**
 * To register a new user.
 * @param userDetails
 * @param sessionId
 */
export const updateUserProfileApi = async (
  userDetails: UserDetailsModel | MemberDetailsModel,
  sessionId?: string,
  callerEmailId?: string,
): Promise<ResponseFormatModel> => {
  let response: ResponseFormatModel = {
    message: "",
    status: HttpStatus.BadRequest,
  };

  try {
    const headers: any = await getAuthHeaders(sessionId, callerEmailId);
    headers["Content-Type"] = "application/json";
    const url = `${BASE_PATH}/user/update`;
    response = await apiRequest(url, "POST", headers, userDetails);
    if (response.status === HttpStatus.OK && response.data) {
      // If the updated profile belongs to the currently logged-in user, refresh the login cookie/session payload
      // so UI (avatars) updates immediately without a full reload.
      try {
        const updated = response.data as UserDetailsModel;
        if (updated?.email_id) {
          const roles = Object.values(AppConstants.Roles);
          for (const roleCookieName of roles) {
            const cookieData =
              CookieSessionManager.getLoginCookie(roleCookieName);
            const payload = cookieData?.value as any;
            if (cookieData && payload?.email === updated.email_id) {
              const nextPayload = {
                ...payload,
                user_img_url: updated.img_url || "",
                firstName: updated.first_name ?? payload.firstName,
                lastName: updated.last_name ?? payload.lastName,
                account_type: updated.account_type ?? payload.account_type,
                premium_data_filled:
                  updated.premium_data_filled ?? payload.premium_data_filled,
              };
              CookieSessionManager.saveLoginData({
                ...cookieData,
                value: nextPayload,
              });
              break;
            }
          }
        }
      } catch (e) {
        logger.warn("Could not refresh login cookie after profile update", {
          error: String(e),
        });
      }
      return response;
    } else {
      logger.error(`Error Updating User Profile -- : ${response}`);
      throw new Error("Error Updating User Profile -- ");
    }
  } catch (error) {
    await logoutOnUnauthorized(response);
    logger.error("Error Updating User Profile - :" + error);
    response = new ResponseFormatModel(500, "Server Error", null);
  }

  return response;
};

/**
 * Updates subscription for guardian and linked trainees (backend clears Redis + notifies connected users).
 */
export const updateSubscriptionPlanApi = async (
  userDetails: UserDetailsModel,
  sessionId?: string,
  callerEmailId?: string,
): Promise<ResponseFormatModel> => {
  let response: ResponseFormatModel = {
    message: "",
    status: HttpStatus.BadRequest,
  };

  try {
    const headers: any = await getAuthHeaders(sessionId, callerEmailId);
    headers["Content-Type"] = "application/json";
    if (userDetails?.email_id) {
      headers["email_id"] = userDetails.email_id;
    }
    const url = `${BASE_PATH}/user/subscription/update`;
    response = await apiRequest(url, "POST", headers, userDetails);
    if (response.status === HttpStatus.OK && response.data) {
      return response;
    }
    logger.error(
      `Error updating subscription plan: ${JSON.stringify(response)}`,
    );
    await logoutOnUnauthorized(response);
    throw new Error("Error updating subscription plan");
  } catch (error) {
    await logoutOnUnauthorized(response);
    logger.error("Error updating subscription plan:", { error: String(error) });
    response = new ResponseFormatModel(500, "Server Error", null);
  }

  return response;
};

export const setUserPlanTierByAdminApi = async (
  payload: {
    email_id: string;
    plan_tier: string;
    plan_start_date?: any;
    plan_end_date?: any;
    beneficiary_trainee_ids?: string[];
  },
  sessionId?: string,
  callerEmailId?: string,
): Promise<ResponseFormatModel> => {
  let response: ResponseFormatModel = {
    message: "",
    status: HttpStatus.BadRequest,
  };

  try {
    // Auth must use the admin caller's email + Firebase token (not the target user's).
    const headers: any = await getAuthHeaders(sessionId, callerEmailId);
    headers["Content-Type"] = "application/json";
    const url = `${BASE_PATH}/user/admin/set-plan-tier`;
    response = await apiRequest(url, "POST", headers, payload);
    if (response.status === HttpStatus.OK && response.data) {
      return response;
    }
    throw new Error(response.message || "Error setting plan tier");
  } catch (error) {
    await logoutOnUnauthorized(response);
    logger.error("Error setting user plan tier:", { error: String(error) });
    return new ResponseFormatModel(500, "Server Error", null);
  }
};

/**
 * To register a new user.
 * @param traineeDetails
 * @param guardianDetails
 * @param memberList
 * @param sessionId
 */
export const updateRegistrationPremiumApi = async (
  traineeDetails: UserDetailsModel,
  guardianDetails: UserDetailsModel,
  memberList: MemberDetailsModel[],
  sessionId?: string,
  callerEmailId?: string,
): Promise<ResponseFormatModel> => {
  let response: ResponseFormatModel = {
    message: "",
    status: HttpStatus.BadRequest,
  };
  const data = {
    trainee: traineeDetails,
    guardian: guardianDetails,
    memberList: memberList,
  };
  try {
    let headers: any = await getAuthHeaders(
      sessionId,
      callerEmailId || guardianDetails.email_id,
    );
    headers["Content-Type"] = "application/json";
    const url = `${BASE_PATH}/user/premium-registration`;
    response = await apiRequest(url, "POST", headers, data);
    if (response.status === HttpStatus.OK && response.data) {
      return response;
    } else {
      logger.error(`Error Updating User - : ${response}`);
      throw new Error("Error Updating User - ");
    }
  } catch (error) {
    logger.error("Error Updating User - :", { error: String(error) });
    await logoutOnUnauthorized(response);
    response = new ResponseFormatModel(500, "Server Error", null);
  }

  return response;
};

/**
 * check if user of type role is present in the system
 * @param adminEmailID
 * @param role Role to check
 */
export const getCountOfAllUserTypes = async (
  user: Pick<UserDetailsModel, "email_id" | "role" | "login_session_id">,
): Promise<ResponseFormatModel> => {
  let response: ResponseFormatModel = {
    message: "",
    status: HttpStatus.BadRequest,
  };
  const { email_id: adminEmailID, role, login_session_id: sessionId } = user;
  const headers = await getAuthHeaders(sessionId, adminEmailID);
  try {
    if (
      adminEmailID &&
      (role === AppConstants.Roles.ADMIN ||
        role === AppConstants.Roles.CENTER_HEAD)
    ) {
      headers["email_id"] = adminEmailID;
      headers["user_id"] = adminEmailID;
      const url = `${BASE_PATH}/count`;
      const userCountResponse = await apiRequest(url, "GET", headers);
      if (userCountResponse.status !== HttpStatus.OK) {
        await logoutOnUnauthorized(userCountResponse);
        throw new Error("Error fetching guardian count -- ");
      }
      return userCountResponse;
    } else if (adminEmailID && role === AppConstants.Roles.GUARDIAN) {
      headers["email_id"] = adminEmailID;
      const url = `${BASE_PATH}/trainees/count`;
      const tCountResponse = await apiRequest(url, "GET", headers);
      if (tCountResponse.status !== HttpStatus.OK) {
        await logoutOnUnauthorized(tCountResponse);
        throw new Error("Error fetching trainee count -- ");
      }
      response = new ResponseFormatModel(HttpStatus.OK, "Success", {
        guardianCount: 0,
        traineeCount: tCountResponse.data,
      });
      return response;
    }
  } catch (error) {
    await logoutOnUnauthorized(response);
    logger.error("Error getCountOfAllUserTypes:", String(error));
  }
  return new ResponseFormatModel(HttpStatus.OK, "Success", {
    guardiansCount: 0,
    traineesCount: 0,
  });
};

/**
 * check if user of type role is present in the system
 * @param email_id Email_Id to check
 */
export const checkIfUserExistsApi = async (
  email_id: string,
): Promise<ResponseFormatModel> => {
  let response: ResponseFormatModel = {
    message: "",
    status: HttpStatus.BadRequest,
  };
  const headers = {
    email_id: email_id,
  };

  try {
    const url = `${BASE_PATH}/user`;
    response = await apiRequest(url, "GET", headers);
    if (response && response.status === HttpStatus.OK && response.data) {
      return response;
    } else {
      throw new Error("Error in checkIfUserExistsApi");
    }
  } catch (error) {
    logger.error("Error in checkIfUserExistsApi:" + error);
    response = new ResponseFormatModel(
      HttpStatus.InternalServerError,
      "Internal Server Error",
    );
  }
  return response;
};

/**
 * Save or update user system details including device ID
 * @param userSystemDetails UserSystemDetailsModel - System details to save
 * @param sessionId Optional session ID
 */
export const saveUserSystemDetailsApi = async (
  userSystemDetails: UserSystemDetailsModel,
  sessionId?: string,
): Promise<ResponseFormatModel> => {
  let response: ResponseFormatModel = {
    message: "",
    status: HttpStatus.BadRequest,
  };
  logger.info(
    "saveUserSystemDetailsApi - userSystemDetails:",
    userSystemDetails,
  );
  try {
    const headers: any = await getAuthHeaders(
      sessionId,
      userSystemDetails.email_id,
    );
    headers["Content-Type"] = "application/json";

    if (userSystemDetails.email_id) {
      headers["email_id"] = userSystemDetails.email_id;
    }

    const url = `${BASE_PATH}/user/system-details`;
    response = await apiRequest(url, "POST", headers, userSystemDetails);

    // Nest POST defaults to 201 unless @HttpCode(200) — accept both as success
    if (
      response.status === HttpStatus.OK ||
      response.status === HttpStatus.Created
    ) {
      return response;
    }
    logger.error(`Error Saving User System Details -- : ${response}`);
    throw new Error(response.message || "Error Saving User System Details -- ");
  } catch (error) {
    logger.error("Error Saving User System Details - :" + error);
    throw error;
  }
};

/**
 * To create a new Center Head by admin.
 * @param userData : UserDetailsModel - New Center Head details
 * @param sessionId : Optional session ID
 */
/**
 * Admin-only: assign an existing guardian to a center head.
 */
export const assignGuardianToCenterHeadApi = async (
  guardianEmailId: string,
  centerHeadEmailId: string,
  sessionId?: string,
  callerEmailId?: string,
): Promise<ResponseFormatModel> => {
  let response: ResponseFormatModel = {
    message: "",
    status: HttpStatus.BadRequest,
  };

  const headersForReq = await getAuthHeaders(sessionId, callerEmailId);
  headersForReq["Content-Type"] = "application/json";

  const url = `${BASE_PATH}/user/admin/assign-guardian-to-center-head`;

  try {
    response = await apiRequest(url, "POST", headersForReq, {
      guardian_email_id: guardianEmailId,
      center_head_email_id: centerHeadEmailId,
    });
    if (response.status === HttpStatus.OK) {
      return response;
    }
    logger.error(`Error assigning guardian to center head -- : ${response}`);
    throw new Error("Error assigning guardian to center head");
  } catch (error) {
    logger.error("Error assigning guardian to center head - :" + error);
    response = new ResponseFormatModel(500, "Server Error", null);
  }

  return response;
};

export const createCenterHeadApi = async (
  userData: UserDetailsModel,
  sessionId?: string,
  callerEmailId?: string,
): Promise<ResponseFormatModel> => {
  let response: ResponseFormatModel = {
    message: "",
    status: HttpStatus.BadRequest,
  };

  const headersForReq = await getAuthHeaders(sessionId, callerEmailId);
  headersForReq["Content-Type"] = "application/json";

  const url = `${BASE_PATH}/user/admin/create-center-head`;

  try {
    response = await apiRequest(url, "POST", headersForReq, userData);
    if (response.status === HttpStatus.OK && response.data) {
      return response;
    } else {
      logger.error(`Error Creating Center Head by Admin -- : ${response}`);
      throw new Error("Error Creating Center Head by Admin -- ");
    }
  } catch (error) {
    logger.error("Error Creating Center Head by Admin - :" + error);
    response = new ResponseFormatModel(500, "Server Error", null);
  }

  return response;
};

/**
 * To delete a user by admin.
 * @param emailToDelete : User's email to delete
 * @param adminEmail : Admin's email executing the deletion
 * @param sessionId : Optional session ID
 */
export const deleteUserByAdminApi = async (
  emailToDelete: string,
  adminEmail: string,
  sessionId?: string,
): Promise<ResponseFormatModel> => {
  let response: ResponseFormatModel = {
    message: "",
    status: HttpStatus.BadRequest,
  };

  const headersForReq = await getAuthHeaders(sessionId, adminEmail);
  headersForReq["Content-Type"] = "application/json";
  headersForReq["email_id"] = adminEmail;

  const data = { email_id: emailToDelete };
  const url = `${BASE_PATH}/user/admin/delete`;

  try {
    response = await apiRequest(url, "POST", headersForReq, data);
    if (response.status === HttpStatus.OK) {
      return response;
    } else {
      logger.error(`Error Deleting User by Admin -- : ${response}`);
      throw new Error("Error Deleting User by Admin -- ");
    }
  } catch (error) {
    logger.error("Error Deleting User by Admin - :" + error);
    response = new ResponseFormatModel(500, "Server Error", null);
  }

  return response;
};
